<script setup lang="ts">
  import { toTypedSchema } from '@vee-validate/zod';
  import { useDebounceFn } from '@vueuse/core';
  import pluralize from 'pluralize';
  import { useForm } from 'vee-validate';
  import { computed, inject, onBeforeMount, ref, shallowRef, useTemplateRef } from 'vue';
  import * as zod from 'zod';

  import type {
    Attachment,
    EmailSender,
    Partner,
    ResponseTaskData,
    ResponseTaskDataJSON,
    Task,
  } from '@stuut/api/models';
  import { HttpError } from '@stuut/common/src/api_client';
  import { formatFileSize } from '@stuut/common/src/utils';
  import { Badge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { Command } from '@ui/components/command';
  import { FormControl, FormField, FormItem } from '@ui/components/form';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { Separator } from '@ui/components/separator';
  import { toast } from '@ui/components/sonner';
  import { TextEditor, TextEditorToolbar } from '@ui/components/text-editor';
  import type { Editor } from '@ui/components/text-editor';

  import { stuut } from '@/api';
  import extractingGif from '@/assets/lil-stuut/extracting.gif';
  import { AttachmentPresignItem } from '@/components/attachments';
  import AttachmentItem from '@/components/attachments/AttachmentItem.vue';
  import AttachmentBar from '@/components/emails/AttachmentBar.vue';
  import EmailRecipientFields from '@/components/emails/EmailRecipientFields.vue';
  import EmailSignature from '@/components/emails/EmailSignature.vue';
  import TaskTemplateGeneration from '@/components/emails/TaskTemplateGeneration.vue';
  import {
    FileDropzoneContent,
    FileDropzoneOverlay,
    FileDropzoneProvider,
  } from '@/components/file-upload-dropzone';
  import TaskAttachment from '@/components/tasks/TaskAttachment.vue';
  import TaskCitations from '@/components/tasks/TaskCitations.vue';
  import { useCreditMemoAttachments } from '@/composables/useCreditMemoAttachments';
  import { useInvoiceAttachments } from '@/composables/useInvoiceAttachments';
  import { MAX_TOTAL_ATTACHMENT_SIZE } from '@/constants/Attachments';
  import { useUsers } from '@/store';

  interface Props {
    completedAction: Record<string, any>;
    disableEditing?: boolean;
    editMode?: boolean;
    emailId?: string;
    emailSenderId?: string;
    ignoreDisabledEffect?: boolean;
    isCompleted?: boolean;
    isNewEmail?: boolean;
    partner?: Partner;
    showSubject?: boolean | undefined;
    stickyToolbar?: boolean;
    task?: Task | undefined;
    taskData: ResponseTaskData;
    validSenders?: EmailSender[] | undefined;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'backToDraft'): void;
  }>();
  const editor = shallowRef<Editor | null>(null);
  const emailToolbarId = ref<string>(`email-toolbar-${props.emailId}`);
  const MIN_GENERATING_MS = 1500;
  const FADE_LAYER_MS = 200;
  const isGenerating = ref(false);
  const isMenuOpen = ref(false);
  const overlayMounted = ref(false);
  const bgVisible = ref(false);
  const gifVisible = ref(false);
  let bgFadeTimeout: ReturnType<typeof setTimeout> | null = null;
  let gifFadeTimeout: ReturnType<typeof setTimeout> | null = null;
  const EditorContainerRef = useTemplateRef('EditorContainerRef');
  const EditorRef = useTemplateRef('EditorRef');
  const AttachmentsRef = useTemplateRef('AttachmentsRef');

  const scrollToAttachments = () => {
    AttachmentsRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const responseData = computed(() => {
    return props.isCompleted && props.completedAction?.output_data
      ? props.completedAction?.output_data
      : props.taskData?.data;
  });

  const editAwareClose = inject<() => void>('editAwareClose');

  const userStore = useUsers();
  const users = computed(() => userStore.users);

  const creditMemoAttachments = useCreditMemoAttachments({
    disabled: () => !!props.disableEditing,
    entity: () => props.task,
    partnerId: () => props.task?.partner_id,
    resource: stuut.tasks,
  });

  const invoiceAttachments = useInvoiceAttachments({
    disabled: () => !!props.disableEditing,
    entity: () => props.task,
    partnerId: () => props.task?.partner_id,
    relatedInvoices: () => props.task?.invoices ?? [],
    resource: stuut.tasks,
  });

  const currentAttachmentsSize = computed<number>(() => props.task?.attachmentsTotalSize ?? 0);

  const isAttachmentBudgetFull = computed<boolean>(
    () => currentAttachmentsSize.value >= MAX_TOTAL_ATTACHMENT_SIZE,
  );

  const onEditorReady = (e: Editor) => {
    editor.value = e;
  };

  const formData = useForm({
    validationSchema: toTypedSchema(
      zod.object({
        body: zod
          .string()
          .min(1)
          .default(responseData?.value?.html_body || ''),
        cc: zod.array(zod.string().email()).default(responseData?.value?.cc || []),
        from: zod
          .string()
          .min(1)
          .default(responseData?.value?.from_ ?? ''),
        subject: zod
          .string()
          .optional()
          .default(responseData?.value?.subject || ''),
        to: zod
          .array(zod.string().email())
          .min(1)
          .default(responseData?.value?.to || []),
      }),
    ),
  });

  const clearFadeTimeouts = () => {
    if (bgFadeTimeout) clearTimeout(bgFadeTimeout);
    if (gifFadeTimeout) clearTimeout(gifFadeTimeout);
    bgFadeTimeout = null;
    gifFadeTimeout = null;
  };

  const handleTemplateSelect = async (templateId: string) => {
    if (!templateId || !props.task?.id || isGenerating.value) return;

    isMenuOpen.value = false;
    clearFadeTimeouts();
    isGenerating.value = true;
    overlayMounted.value = true;
    bgVisible.value = true;
    gifVisible.value = true;
    const startedAt = Date.now();

    try {
      const response = await stuut.tasks.generateEmail(props.task, templateId);

      const emailData = response?.data?.find((data: any) => data.type === 'response')?.data;

      if (!emailData) {
        toast.error('Failed to generate email body');
        return;
      }

      updateEmail(emailData);
    } catch (err) {
      const message =
        err instanceof HttpError ? err.clientMessage : 'Failed to generate email body';
      toast.error(message || 'Failed to generate email body');
      console.error(err);
    } finally {
      const remaining = Math.max(0, MIN_GENERATING_MS - (Date.now() - startedAt));
      setTimeout(() => {
        isGenerating.value = false;
        bgVisible.value = false;
        bgFadeTimeout = setTimeout(() => {
          gifVisible.value = false;
          gifFadeTimeout = setTimeout(() => {
            overlayMounted.value = false;
          }, FADE_LAYER_MS);
        }, FADE_LAYER_MS);
      }, remaining);
    }
  };

  const updateEmail = (responseData: ResponseTaskDataJSON) => {
    if (responseData?.html_body) {
      editor.value?.commands.setContent(responseData.html_body);
    }

    formData.resetForm({
      values: {
        body: responseData?.html_body ?? formData.values.body,
        subject: responseData?.subject ?? formData.values.subject,
        to: responseData?.to ?? formData.values.to,
        cc: responseData?.cc ?? formData.values.cc,
        from: responseData?.from_ ?? formData.values.from,
      },
    });
  };

  const handleNewFileAttachments = (newAttachments: Attachment[]) => {
    if (!newAttachments?.length || !props.task) return;

    props.task.attachments = [...(props.task?.attachments || []), ...newAttachments];
  };

  onBeforeMount(() => {
    if (props.partner) userStore.fetchUsers();
  });

  const save = async () => {
    if (!props.task) return;

    try {
      await stuut.tasks.saveResponse(props.task, {
        cc: formData.values.cc,
        body: formData.values.body,
        html_body: formData.values.body,
      });
    } catch (err) {
      const message = err instanceof HttpError ? err.clientMessage : 'Failed to save draft';
      toast.error(message || 'Failed to save draft');
      console.error(err);
    }
  };

  const debouncedSave = useDebounceFn(save, 500);

  const updateField = (field: 'from' | 'to' | 'cc' | 'subject' | 'body', value: any) => {
    formData.setFieldValue(field, value);
    debouncedSave();
  };

  defineExpose({
    container: EditorContainerRef,
    editor: EditorRef,
    formData,
    isGenerating,
    updateEmail,
  });
</script>

<template>
  <FileDropzoneProvider
    v-slot="{ openFileInput }"
    :max-total-size="MAX_TOTAL_ATTACHMENT_SIZE"
    :current-attachments-size="currentAttachmentsSize"
    @on-new-file-attachments="handleNewFileAttachments"
  >
    <div class="flex flex-col bg-background" :class="editMode ? '' : 'border rounded-lg shadow-md'">
      <form
        class="flex flex-col h-full w-full flex-1 min-h-0"
        as="div"
        @keydown.enter="(e: Event) => e.preventDefault()"
        @keydown.escape="editAwareClose"
      >
        <div class="flex flex-col px-6 space-y-2.5 pt-2">
          <Button
            v-if="editMode"
            variant="ghost"
            size="sm"
            class="self-start -ml-2.5"
            @click="emit('backToDraft')"
          >
            <i class="far fa-arrow-left"></i>
            Exit edit mode
          </Button>

          <EmailRecipientFields
            class="pb-4"
            :from-email="formData?.values?.from ?? responseData?.from_ ?? ''"
            :valid-senders="validSenders ?? []"
            :to="formData?.values?.to ?? []"
            :cc="formData?.values?.cc ?? []"
            :subject="formData?.values?.subject ?? ''"
            :partner="partner"
            :users="users"
            :hide-subject="!showSubject"
            :readonly-subject="!isNewEmail"
            :suggest-contacts="true"
            :disabled="disableEditing || isGenerating"
            @update:from-email="(v) => updateField('from', v)"
            @update:to="(v) => updateField('to', v)"
            @update:cc="(v) => updateField('cc', v)"
            @update:subject="(v) => updateField('subject', v)"
          />
        </div>

        <div
          v-if="editor && !disableEditing && props.task"
          class="flex items-center gap-2 w-full border-y py-2 px-3.5"
        >
          <TextEditorToolbar :editor="editor" />

          <Separator class="h-5 my-auto mx-0" orientation="vertical" />

          <Popover v-model:open="isMenuOpen">
            <PopoverTrigger as-child>
              <Button
                type="button"
                size="xs"
                variant="outline"
                :disabled="isGenerating"
                class="hover:bg-agent/10 hover:border-agent/20"
                @click.stop.prevent
              >
                <LoadingSpinner v-if="isGenerating" class="text-xs" />
                <i v-else class="fas fa-wand-magic-sparkles text-2xs!" />
                <span>{{ isGenerating ? 'Generating…' : 'Generate' }}</span>
                <i class="fas fa-chevron-down text-2xs!" />
              </Button>
            </PopoverTrigger>

            <PopoverContent class="w-72 p-0" align="start">
              <Command>
                <TaskTemplateGeneration
                  :disabled="disableEditing"
                  :is-generating="isGenerating"
                  @select="handleTemplateSelect"
                />
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <FileDropzoneContent class="relative flex flex-1 flex-col min-h-0 overflow-y-auto">
          <div
            ref="EditorContainerRef"
            class="email-editor-body flex flex-col flex-1 w-full relative"
          >
            <div
              v-if="overlayMounted"
              class="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none"
            >
              <div
                class="absolute inset-0 bg-background/60 transition-opacity duration-200"
                :class="{ 'opacity-0': !bgVisible }"
              />
              <img
                :src="extractingGif"
                alt="Generating"
                class="size-35 relative transition-opacity duration-200"
                :class="{ 'opacity-0': !gifVisible }"
              />
            </div>

            <FormField name="body">
              <FormItem
                v-auto-animate
                class="form-item space-y-1.5 flex flex-1 w-full min-h-0 px-2.5"
              >
                <FormControl>
                  <TextEditor
                    ref="EditorRef"
                    :model-value="formData.values.body"
                    class="border-0! flex flex-1 pt-5 pb-2 min-h-35 h-full w-full px-2"
                    :class="{
                      'cursor-default': disableEditing || isGenerating,
                      'opacity-60': disableEditing && !ignoreDisabledEffect,
                    }"
                    :toolbar="`#${emailToolbarId}`"
                    :disabled="disableEditing || isGenerating"
                    :disable-editing="disableEditing || isGenerating"
                    @update:model-value="(event) => updateField('body', event)"
                    @ready="onEditorReady"
                  />
                </FormControl>
              </FormItem>
            </FormField>
          </div>

          <div class="flex items-baseline justify-between w-full pr-6 py-2">
            <EmailSignature
              v-if="!completedAction"
              class="px-4.5 py-2"
              :email-from="formData?.values?.from"
              :created-at="task?.created_at"
              :task="task"
            />

            <TaskCitations
              v-if="task?.knowledge_citations?.length"
              :citations="task.knowledge_citations"
            />
          </div>

          <div
            v-if="props.task?.attachments?.length"
            class="flex flex-col gap-2.5 pt-2 pb-6 w-full px-4 space-y-2"
          >
            <div ref="AttachmentsRef" class="flex flex-col gap-2.5 w-full">
              <div class="flex items-center justify-between">
                <span class="font-semibold block text-xs text-muted-foreground"> Attached: </span>
                <span
                  class="text-xs text-muted-foreground"
                  :class="isAttachmentBudgetFull && 'text-destructive'"
                >
                  {{ formatFileSize(currentAttachmentsSize) }} of
                  {{ formatFileSize(MAX_TOTAL_ATTACHMENT_SIZE) }}
                </span>
              </div>

              <div role="list" class="flex flex-wrap gap-2 items-center">
                <AttachmentPresignItem
                  v-for="(attachment, index) in props.task.attachments"
                  :key="index"
                  role="listitem"
                  :attachment="attachment"
                  :entity-model="props.task"
                >
                  <TaskAttachment
                    :task="props.task"
                    :attachment
                    :hide-delete="disableEditing"
                    icon-left="fa-solid fa-paperclip"
                  />
                </AttachmentPresignItem>
              </div>
            </div>

            <div
              v-if="
                !props.disableEditing &&
                ((props.task?.invoices?.length && props.task?.attachableInvoices?.length) ||
                  creditMemoAttachments.attachableCreditMemos.value.length)
              "
              class="flex flex-col gap-2.5 w-full"
            >
              <span class="font-semibold block text-xs text-muted-foreground">Suggested:</span>
              <div role="list" class="flex flex-wrap gap-2 items-center">
                <AttachmentItem
                  v-for="invoice in props.task?.attachableInvoices"
                  :key="`inv-${invoice.id}`"
                  role="listitem"
                  :dashed="true"
                  :show-left-icon="false"
                  :show-right-icon="!props.disableEditing"
                  :right-icon-hover="true"
                  right-icon="fa-plus-circle"
                  :uploading="invoiceAttachments.isInvoicePending(invoice)"
                  @right-icon-click="invoiceAttachments.toggleInvoiceAttachment(invoice)"
                >
                  <template #preview_trigger>
                    <span>{{ invoice.number }}</span>
                  </template>

                  <template v-if="invoice.pdf" #preview_content>
                    <img :src="invoice.pdf" :alt="invoice.number || ''" />
                  </template>
                </AttachmentItem>

                <AttachmentItem
                  v-for="cm in creditMemoAttachments.attachableCreditMemos.value"
                  :key="`cm-${cm.id}`"
                  role="listitem"
                  :dashed="true"
                  :show-left-icon="false"
                  :show-right-icon="!props.disableEditing"
                  :right-icon-hover="true"
                  right-icon="fa-plus-circle"
                  :uploading="creditMemoAttachments.isCreditMemoPending(cm)"
                  @right-icon-click="creditMemoAttachments.toggleAttachCreditMemo(cm)"
                >
                  <template #preview_trigger>
                    <span>{{ cm.number }}</span>
                  </template>

                  <template v-if="cm.pdf" #preview_content>
                    <img :src="cm.pdf" :alt="cm.number || ''" />
                  </template>
                </AttachmentItem>
              </div>
            </div>
          </div>

          <FileDropzoneOverlay />
        </FileDropzoneContent>

        <div
          v-if="!disableEditing && props.task"
          class="flex flex-row items-center bg-background border-t px-4 py-1 rounded-bl-xl rounded-br-xl"
          :class="stickyToolbar && 'sticky bottom-0 z-10'"
        >
          <AttachmentBar
            :invoice-attachments="invoiceAttachments"
            :credit-memo-attachments="creditMemoAttachments"
            :related-invoices="props.task?.invoices ?? []"
            :credit-memo-enabled="true"
            :open-file-input="openFileInput"
            :is-attachment-budget-full="isAttachmentBudgetFull"
          />

          <div v-if="task.attachments?.length" class="flex">
            <Separator class="h-5 my-auto mx-0" orientation="vertical" />

            <Badge class="cursor-pointer ml-2.5" variant="secondary" @click="scrollToAttachments">
              <i class="fas fa-paperclip text-2xs" />
              <span class="hidden xl:block">{{
                pluralize('attachments', task.attachments?.length, true)
              }}</span>

              <span class="xl:hidden">{{ task.attachments?.length }}</span>
            </Badge>
          </div>

          <div class="flex items-center justify-end flex-1 gap-x-2">
            <slot name="sendAction" />
          </div>
        </div>
      </form>
    </div>
  </FileDropzoneProvider>
</template>

<style lang="postcss">
  @reference "../../globals.css";

  .item-details p:nth-child(odd) {
    @apply text-muted-foreground;
  }

  .item-details p:nth-child(even) {
    @apply text-foreground font-medium;
  }
</style>
